import { ReducerManager, Store } from '@ngrx/store';
import { computed, inject, Injectable } from '@angular/core';
import { combineLatest, EMPTY, forkJoin, Observable } from 'rxjs';
import { defaultIfEmpty, map, switchMap, take, tap } from 'rxjs/operators';
import { DataModel } from '../../data/data.model';
import { DataService } from '../../data/data.service';
import { ConsentApiService } from '../api/consent-api.service';
import { UserService } from '../user.service';
import { ConsentPermissionAdapter } from './consent-permission.adapter';
import { selectPatientIdRoot } from '../../store/selectors/app-config.selectors';
import { UserInformationProvider } from '@xt/user-context';
import {
  ConsentPermission,
  ConsentPermissionResponse,
  ConsentPolicy,
} from './consent.interface';

const arrConcatUniques = (...arrays: any[][]) =>
  arrays.reduce(
    (acc, arr) => [...acc, ...arr.filter((item) => !acc.includes(item))],
    [],
  );

export interface ConsentDataService<Model extends DataModel>
  extends DataService<Model> {
  dataWithConsent$: Observable<Model[]>;
  dataWithoutConsent$: Observable<Model[]>;
  selectedDataWithConsent$: Observable<Model[]>;
  selectedDataWithoutConsent$: Observable<Model[]>;
  init(providerId: string): void;
  addConsent(data: Model[]): void;
  removeConsent(data: Model[]): void;
  init$(providerId: string): Observable<void>;
}

@Injectable({ providedIn: 'root' })
export class ConsentService extends DataService<ConsentPermission> {
  private userContext = inject(UserInformationProvider);

  private contextLabels = [
    'PatientList',
    // 'readDocument',
    // 'readDiaryEntry',
    // 'readQuestionnaire',
    // 'readLogbook',
  ];

  private policies: ConsentPolicy[] = [];
  public patientId: string;
  public patientRootId: string;

  private readonly patient = computed(() => this.userContext.patient());

  constructor(
    private userService: UserService,
    private adapter: ConsentPermissionAdapter,
    private consentApi: ConsentApiService,
    private sessionStore: Store,
    protected manager: ReducerManager,
  ) {
    super('consent-permissions', sessionStore, manager);
    //fetch consent data on service init
    this.fetchConsentData$().pipe(take(1)).subscribe();
  }

  public refreshPermissions(contextLabel: string = 'PatientList'): void {
    this.searchPermissions$(this.patientId, contextLabel).subscribe(
      (permissions: ConsentPermission[]) => {
        permissions.forEach((permission) => {
          if (this.hasEntity(permission.id)) {
            this.updateStoreItem(permission.id, permission);
          } else {
            this.addStoreItem(permission);
          }
        });
      },
    );
  }

  public fetchConsentData$() {
    return combineLatest([
      this.userService.resourceType$,
      this.userService.resourceId$,
      this.sessionStore.pipe(selectPatientIdRoot),
    ]).pipe(
      switchMap(([type, id, rootPatientId]) => {
        if (type === 'Patient') {
          this.patientId = id;
          this.patientRootId = rootPatientId;

          return this.fetchPoliciesAndPermissions$(id);
        } else if (type === 'Representative' || type === 'Person') {
          this.patientId = this.patient().id;

          this.patientRootId = (
            this.patient().identifier as any
          ).system?.replace('urn:oid:', '');

          return this.fetchPoliciesAndPermissions$(this.patient().id);
        }
        // Return an empty observable for other user types
        return EMPTY;
      }),
    );
  }

  private fetchPoliciesAndPermissions$(
    patientId,
    contextLabel: string = 'PatientList',
  ) {
    return forkJoin(
      this.contextLabels.map((contextLabel) =>
        this.searchPolicies$(contextLabel),
      ),
    ).pipe(
      defaultIfEmpty([]),
      map((policiesSets: ConsentPolicy[][]) => {
        this.policies = policiesSets.reduce(
          (acc, policies) => acc.concat(policies),
          [],
        );
      }),
      switchMap(() => {
        return this.searchPermissions$(patientId, contextLabel).pipe(
          tap((permissions) => {
            permissions.forEach((permission) => {
              if (this.hasEntity(permission.id)) {
                this.updateStoreItem(permission.id, permission);
              } else {
                this.addStoreItem(permission);
              }
            });
          }),
        );
      }),
    );
  }

  private searchPolicies$(contextLabel: string): Observable<ConsentPolicy[]> {
    return this.consentApi.searchPolicies(contextLabel);
  }

  private searchPermissions$(
    patientId: string,
    contextLabel?: string,
  ): Observable<ConsentPermission[]> {
    return this.consentApi
      .searchPermissions(patientId, contextLabel)
      .pipe(
        switchMap((response: ConsentPermissionResponse[]) =>
          forkJoin(
            response.map((resource: ConsentPermissionResponse) =>
              this.adapter.fromResource$(resource),
            ),
          ),
        ),
      );
  }

  private addPermission(permission: ConsentPermission, contextLabel?): void {
    this.consentApi
      .createPermission(
        this.adapter.toResource(
          permission,
          this.patientId,
          this.patientRootId,
          contextLabel,
          true,
        ),
      )
      .subscribe(() => {
        this.refreshPermissions(contextLabel);
      });
  }

  private updatePermission(permission: ConsentPermission, contextLabel?): void {
    this.consentApi
      .updatePermission(
        permission.id,
        this.adapter.toResource(
          permission,
          this.patientId,
          this.patientRootId,
          contextLabel,
        ),
      )
      .subscribe(() => {
        this.refreshPermissions(contextLabel);
      });
  }

  public getPermissions$(
    contextLabel?: string,
    providerId?: string,
  ): Observable<ConsentPermission[]> {
    return this.data$.pipe(
      map((permissions) =>
        permissions.filter(
          (permission) =>
            (contextLabel
              ? permission.contextLabels?.includes(contextLabel)
              : true) &&
            (providerId
              ? permission.providerId === providerId ||
                permission.facilityId === providerId
              : true),
        ),
      ),
    );
  }

  public getConsentEntityIds$(
    providerId: string,
    contextLabel?: string,
  ): Observable<string[]> {
    return this.getPermissions$(contextLabel, providerId).pipe(
      map((permissions) =>
        permissions.reduce(
          (acc, permission) =>
            arrConcatUniques(acc, permission.entityIds || []),
          [],
        ),
      ),
    );
  }

  private newPermission({
    providerId,
    entityIds,
    policy,
    validFrom = '',
    entities = [],
  }: {
    providerId: string;
    entityIds: string[];
    policy: ConsentPolicy;
    validFrom?: string;
    entities: any;
  }): ConsentPermission {
    return {
      description: '',
      validFrom,
      validTill: '',
      providerId,
      facilityId: null,
      policyId: policy['policyId'],
      contextLabels: policy.contextLabels,
      entityIds,
      entities,
    };
  }

  public addConsent(
    providerId: string,
    entityIds: string[],
    contextLabel: string,
    validFrom?: string,
    entities?: any,
  ): void {
    const policy = this.policies.find(({ contextLabels }) =>
      contextLabels.includes(contextLabel),
    );

    if (!policy) {
      alert('No policy found for contextLabel ' + contextLabel);
      return;
    }

    let permission;
    this.getPermissions$(contextLabel, providerId)
      .pipe(take(1))
      .subscribe((permissions) => (permission = permissions[0]));

    if (!permission) {
      const newPermission = this.newPermission({
        providerId,
        entityIds,
        entities,
        policy,
        ...(validFrom ? { validFrom } : null),
      });

      this.addPermission(newPermission, contextLabel);
    } else {
      const newPermission = {
        ...permission,
        entityIds: arrConcatUniques(permission.entityIds || [], entityIds),
        entities,
        ...(validFrom ? { validFrom } : null),
      };
      this.updatePermission(newPermission, contextLabel);
    }
  }

  public removeConsent(
    providerId: string,
    entityIds: string[],
    contextLabel: string,
    entities?: any,
  ): void {
    const policy = this.policies.find(({ contextLabels }) =>
      contextLabels.includes(contextLabel),
    );

    if (!policy) {
      /** @todo handle case if there is no policy */
      return;
    }

    let permission;
    this.getPermissions$(contextLabel, providerId)
      .pipe(take(1))
      .subscribe((permissions) => (permission = permissions[0]));

    if (!permission) {
      /** @todo handle case if there is no permission */
    } else {
      const newPermission = {
        ...permission,
        entities,
        entityIds: permission.entityIds.filter((id) => !entityIds.includes(id)),
      };

      if (
        newPermission.entityIds?.length == 0 ||
        newPermission.entities?.length == 0
      ) {
        this.removePermissions(providerId, contextLabel);
      } else {
        this.updatePermission(newPermission, contextLabel);
      }
    }
  }

  public removePermissions(providerId: string, contextLabel: string) {
    this.getPermissions$(contextLabel, providerId)
      .pipe(take(1))
      .subscribe((permissions) => {
        forkJoin(
          permissions.map((permission) =>
            this.consentApi
              .deletePermission(permission)
              .pipe(tap(() => this.removeById(permission.id))),
          ),
        )
          .pipe(defaultIfEmpty([]))
          .subscribe({
            next: () => this.refreshPermissions(contextLabel),
            error: (err) => console.error(err),
          });
      });
  }
}
